/**
 * TaskTemplatesDialog — CRUD modèles de tâche + instanciation rapide.
 */
import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Copy, FileStack, Rocket } from "lucide-react";
import { toast } from "sonner";

const PRIOS = [
  { v: "low", l: "Faible" }, { v: "medium", l: "Normale" },
  { v: "high", l: "Haute" }, { v: "urgent", l: "Urgente" },
];

export default function TaskTemplatesDialog({ open, onOpenChange, onInstantiated }) {
  const [templates, setTemplates] = useState([]);
  const [name, setName] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("medium");
  const [tagsStr, setTagsStr] = useState("");
  const [checklistStr, setChecklistStr] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/tasks/templates");
      setTemplates(r.data.templates || []);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { if (open) load(); }, [open]);

  const create = async () => {
    if (!name.trim() || !title.trim()) return;
    setSaving(true);
    try {
      const tags = tagsStr.split(",").map(t => t.trim().replace(/^#/, "")).filter(Boolean);
      const checklist = checklistStr.split("\n").map(l => l.trim()).filter(Boolean);
      await api.post("/tasks/templates", {
        name, title, description: description || null,
        priority, tags, checklist, default_status: "todo",
      });
      setName(""); setTitle(""); setDescription(""); setTagsStr(""); setChecklistStr("");
      await load();
      toast.success("Modèle créé");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const instantiate = async (tpl) => {
    try {
      const r = await api.post(`/tasks/templates/${tpl.id}/instantiate`, {});
      toast.success(`Tâche créée depuis "${tpl.name}"`);
      onInstantiated?.(r.data);
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce modèle ?")) return;
    try {
      await api.delete(`/tasks/templates/${id}`);
      await load();
      toast.success("Modèle supprimé");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="templates-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileStack className="w-5 h-5" />Modèles de tâche</DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left: create */}
          <div className="space-y-2 p-3 border border-slate-200 rounded-lg">
            <p className="text-sm font-semibold">Nouveau modèle</p>
            <Input placeholder="Nom (ex : Mise en route client)" value={name} onChange={e => setName(e.target.value)} data-testid="tpl-name" />
            <Input placeholder="Titre de la tâche générée" value={title} onChange={e => setTitle(e.target.value)} data-testid="tpl-title" />
            <Textarea rows={2} placeholder="Description (optionnel)" value={description} onChange={e => setDescription(e.target.value)} data-testid="tpl-desc" />
            <div className="flex gap-2">
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger className="flex-1" data-testid="tpl-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRIOS.map(p => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input placeholder="tags (virgule)" value={tagsStr} onChange={e => setTagsStr(e.target.value)} className="flex-1" data-testid="tpl-tags" />
            </div>
            <Textarea rows={3} placeholder="Checklist — 1 ligne = 1 étape" value={checklistStr} onChange={e => setChecklistStr(e.target.value)} data-testid="tpl-checklist" />
            <Button onClick={create} disabled={saving || !name.trim() || !title.trim()} className="w-full" data-testid="tpl-create-btn">
              <Plus className="w-4 h-4 mr-2" />Créer le modèle
            </Button>
          </div>

          {/* Right: list */}
          <div className="space-y-2 max-h-[500px] overflow-y-auto">
            <p className="text-sm font-semibold">Modèles existants ({templates.length})</p>
            {templates.length === 0 && <p className="text-sm text-slate-400 text-center py-8">Aucun modèle</p>}
            {templates.map(t => (
              <div key={t.id} className="p-3 border border-slate-200 rounded-lg hover:border-slate-300" data-testid={`tpl-row-${t.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{t.name}</p>
                    <p className="text-xs text-slate-500 truncate">{t.title}</p>
                  </div>
                  <Badge variant="outline" className="text-[10px] shrink-0">{t.usage_count} usages</Badge>
                </div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {t.tags?.slice(0, 4).map(tg => <Badge key={tg} variant="outline" className="text-[10px]">#{tg}</Badge>)}
                  {t.checklist?.length > 0 && <Badge variant="outline" className="text-[10px]"><Copy className="w-2 h-2 mr-1" />{t.checklist.length} étapes</Badge>}
                </div>
                <div className="flex gap-1 mt-2">
                  <Button variant="default" size="sm" onClick={() => instantiate(t)} className="h-7 flex-1" data-testid={`tpl-use-${t.id}`}>
                    <Rocket className="w-3 h-3 mr-1" />Créer tâche
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(t.id)} className="h-7 text-red-500" data-testid={`tpl-del-${t.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
